/**
 * PlayDemo.js
 * Output step of the Sales Studio editors: one email frame showing how the
 * image sits in the mail, four random real contacts next to it, and a
 * "Generate demo output" button that renders the current (unsaved) settings
 * for the picked contact.
 */

import React, { useEffect, useMemo, useState } from "react";
import { loadDemoContacts, pickRandomContacts, pinDemoImage, renderDemoImage } from "./salesPlayDemo";

const hostOf = (url) => {
  try {
    return new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.replace(/^www\./, "");
  } catch (_) {
    return "";
  }
};

const firstNameOf = (contact) =>
  (contact && (contact.lead_firstname || String(contact.lead_name || "").split(" ")[0])) || "";

const PlayDemo = ({ userId, config, frame, onFrame, pieces, model, credits, renderExample, mailText, onPin, onDemo, onSelectTake, onKeep, footNote = "", displayWidth = null, align = "center" }) => {
  const [pool, setPool] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState(null);
  const [pieceKey, setPieceKey] = useState(pieces[0]?.key || null);
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pinning, setPinning] = useState(false);

  useEffect(() => {
    let alive = true;
    loadDemoContacts(userId, { needsWebsite: true })
      .then((list) => {
        if (!alive) return;
        setPool(list);
        setContacts(pickRandomContacts(list));
      })
      .catch(() => alive && setPool([]));
    return () => {
      alive = false;
    };
  }, [userId]);

  const piece = pieces.find((p) => p.key === pieceKey) || pieces[0];
  const contact = contacts.find((c) => c.id === contactId) || null;
  const resultKey =
    contact && piece ? `${contact.id}::${piece.key}::${model}::${JSON.stringify(piece.draftAsset)}` : "";
  const result = resultKey ? results[resultKey] : null;
  const pieceCredits = piece ? credits(piece) : 0;
  const greeting = useMemo(() => {
    const name = firstNameOf(contact);
    return name ? `Hi ${name},` : "Hi there,";
  }, [contact]);

  const shuffle = () => {
    if (!pool) return;
    setContacts(pickRandomContacts(pool));
    setContactId(null);
  };

  const generate = async () => {
    if (!contact || !piece) return;
    setBusy(true);
    setError("");
    try {
      const rendered = await renderDemoImage({ userId, draftAsset: piece.draftAsset, contact });
      setResults((prev) => ({ ...prev, [resultKey]: rendered }));
      if (onDemo) {
        await onDemo({
          key: piece.key,
          imageUrl: rendered.imageUrl,
          storagePath: rendered.storagePath || "",
          forName: contact?.business_name || "",
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const pinnedHere = !!(result && piece?.pinned && result.pinnedUrl === piece.pinned.imageUrl);

  const pin = async () => {
    if (!result?.storagePath || !piece) return;
    setPinning(true);
    setError("");
    try {
      const kept = await pinDemoImage({ userId, storagePath: result.storagePath });
      setResults((prev) => ({ ...prev, [resultKey]: { ...result, pinnedUrl: kept.imageUrl } }));
      onPin(piece.key, { ...kept, forName: contact?.business_name || "" });
    } catch (err) {
      setError(err.message);
    } finally {
      setPinning(false);
    }
  };

  return (
    <div className="wr-demo">
      <div className="wr-demoMain">
        <div className="wr-demoFrames" role="radiogroup" aria-label="Presentation">
          {config.frames.map((f) => (
            <button
              type="button"
              key={f.id}
              role="radio"
              aria-checked={frame === f.id}
              aria-label={f.label}
              className={`wr-demoFrame ${frame === f.id ? "active" : ""}`}
              onClick={() => onFrame(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="wr-demoMail">
          <div className="wr-mailWho">
            <span className="wr-mailAvatar" />
            <span>
              <b>You</b>
              <em>to {contact ? contact.business_name : "your contact"}</em>
            </span>
          </div>
          <p className="wr-demoText">
            {greeting}
            <br />
            {mailText}
          </p>
          <div
            className={`wr-demoImage ${piece?.shownUrl || result ? "is-live" : ""} ${displayWidth ? "is-sized" : ""} is-${align}`}
            style={displayWidth ? { maxWidth: displayWidth } : undefined}
          >
            {piece?.shownUrl || result?.imageUrl ? (
              <img src={piece?.shownUrl || result.imageUrl} alt="Saved GIF" />
            ) : (
              <>
                {renderExample(frame, piece)}
                <span className="wr-demoBadge">{busy ? "Generating…" : "Example"}</span>
              </>
            )}
          </div>
          {(piece?.takes || []).length > 0 && (
            <div className="wr-takes" role="listbox" aria-label="Recent GIFs">
              {piece.takes.map((take, index) => {
                const selected = take.id === piece.selectedTakeId;
                return (
                  <div key={take.id} className={`wr-take ${selected ? "is-selected" : ""} ${selected && piece.fixed ? "is-fixed" : ""}`}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      aria-label={`GIF ${index + 1}`}
                      onClick={() => onSelectTake && onSelectTake(piece.key, take.id)}
                    >
                      <img src={take.imageUrl} alt="" />
                    </button>
                    {selected && onKeep && (
                      <button
                        type="button"
                        className="wr-takeFix"
                        aria-pressed={!!piece.fixed}
                        onClick={() => onKeep(!piece.fixed)}
                      >
                        {piece.fixed ? "Sending to everyone" : "Send to everyone"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <p className="wr-demoText">Happy to walk you through it.</p>
        </div>
        <div className="wr-demoActions">
          <button
            type="button"
            className="altBtn altBtn--save"
            onClick={generate}
            disabled={!contact || busy || !piece}
          >
            <i className={`fa-solid ${busy ? "fa-spinner fa-spin" : "fa-wand-magic-sparkles"}`} />
            {busy ? "Generating…" : "Generate demo output"}
          </button>
          <span className="wr-demoCost">
            {contact
              ? `${Number(pieceCredits.toFixed(2))} credits, for ${contact.business_name}`
              : "Pick a contact on the right first."}
          </span>
        </div>
        {onPin && result?.storagePath && (
          <div className={`wr-demoPin ${pinnedHere ? "is-on" : ""}`} role="group" aria-label="Use this GIF every time">
            {pinnedHere ? (
              <>
                <span className="wr-demoPinState">
                  <i className="fa-solid fa-thumbtack" aria-hidden="true" /> Used every time
                </span>
                <button type="button" className="altBtn altBtn--petrol" onClick={() => onPin(piece.key, null)}>
                  <i className="fa-solid fa-wand-magic-sparkles" /> Make one per contact
                </button>
              </>
            ) : (
              <button type="button" className="altBtn altBtn--petrol" onClick={pin} disabled={pinning}>
                <i className={`fa-solid ${pinning ? "fa-spinner fa-spin" : "fa-thumbtack"}`} />
                {pinning ? "Keeping it…" : "Use every time"}
              </button>
            )}
            <span className="wr-demoCost">
              {pinnedHere
                ? "Every contact gets exactly this GIF. No new render and no credits per send. Save the play to keep it."
                : "Like this one? Send exactly this GIF to every contact instead of a new one each time. No credits per send."}
              {piece.perContact &&
                ` Heads up: it shows ${contact?.business_name || "this contact"}'s own branding or place, and every contact would see that instead of their own.`}
            </span>
          </div>
        )}
        {error && <div className="il-msg">{error}</div>}
        {result?.scene && (
          <div className="wr-demoFound" role="group" aria-label="Found on their website">
            <span className="il-fieldLabel">Found on their website</span>
            <div className="wr-demoFoundRow">
              {[
                ["building", "Their building"],
                ["town", "Their town"],
              ].map(
                ([slot, label]) =>
                  result.scene[slot] && (
                    <figure className="wr-demoFoundShot" key={slot}>
                      <img src={result.scene[slot].url} alt={label} referrerPolicy="no-referrer" />
                      <figcaption>
                        <b>{label}</b>
                        {result.scene[slot].shows}
                      </figcaption>
                    </figure>
                  ),
              )}
            </div>
            {result.scene.details && (
              <p className="wr-demoText">
                <b>Typical details:</b> {result.scene.details}
              </p>
            )}
          </div>
        )}
      </div>
      <aside className="wr-demoSide">
        <div className="wr-demoSideHead">
          <span className="il-fieldLabel">Test on a real contact</span>
          <button type="button" className="wr-demoShuffle" onClick={shuffle} disabled={!pool || !pool.length}>
            <i className="fa-solid fa-shuffle" /> Others
          </button>
        </div>
        {pieces.length > 1 && (
          <div className="wr-demoPieces" role="radiogroup" aria-label="Which image">
            {pieces.map((p) => (
              <button
                type="button"
                key={p.key}
                role="radio"
                aria-checked={p.key === piece?.key}
                className={`wr-demoFrame ${p.key === piece?.key ? "active" : ""}`}
                onClick={() => setPieceKey(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
        {pool === null ? (
          <p className="wr-demoEmpty">Loading contacts…</p>
        ) : contacts.length === 0 ? (
          <p className="wr-demoEmpty">No contacts with a website yet. Add one to test the play live.</p>
        ) : (
          <div className="wr-demoContacts" role="radiogroup" aria-label="Demo contact">
            {contacts.map((c) => (
              <button
                type="button"
                key={c.id}
                role="radio"
                aria-checked={c.id === contactId}
                className={`wr-demoContact ${c.id === contactId ? "active" : ""}`}
                onClick={() => setContactId(c.id)}
              >
                <b>{c.business_name}</b>
                <span>{[c.lead_city, hostOf(c.lead_website)].filter(Boolean).join(" · ")}</span>
              </button>
            ))}
          </div>
        )}
        <p className="wr-demoNote">
          {footNote || "We render it exactly like a real send: their website, their info, your current settings. Nothing is saved."}
        </p>
      </aside>
    </div>
  );
};

export default PlayDemo;
